import { FC, useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoucherSearch, VoucherList, ManualVoucherModal } from './components';
import { vouchersService } from './services/vouchers.service';
import { VoucherSearchFilters, VoucherSummary } from './types';

export const VoucherManagementPage: FC = () => {
  const [vouchers, setVouchers] = useState<VoucherSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isNewVoucherModalOpen, setIsNewVoucherModalOpen] = useState(false);

  const handleSearch = async (filters: VoucherSearchFilters) => {
    setLoading(true);
    
    try {
      const response = await vouchersService.searchVouchers(filters);
      setVouchers(response.data);
      
      toast.success(`Se encontraron ${response.data.length} comprobantes`);
    } catch (error) {
      console.error('Error al buscar comprobantes:', error);
      toast.error('Error al buscar comprobantes', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (voucherId: string, reason: string) => {
    setCancelLoading(true);
    
    try {
      const response = await vouchersService.cancelVoucher({ voucherId, reason });
      
      if (response.success) {
        toast.success('Comprobante anulado exitosamente', {
          description: response.sunatTicketNumber 
            ? `Ticket SUNAT: ${response.sunatTicketNumber}`
            : response.message
        });
        
        // Actualizar la lista de comprobantes
        setVouchers(prev => prev.map(voucher => 
          voucher.id === voucherId 
            ? { ...voucher, status: 'anulado' as const, canCancel: false }
            : voucher
        ));
      } else {
        throw new Error(response.error || 'Error en la anulación');
      }
    } catch (error) {
      console.error('Error al anular comprobante:', error);
      toast.error('Error al anular comprobante', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleNewVoucherSuccess = () => {
    // VoucherSearch disparará la búsqueda con sus filtros actuales
    // Simplemente refrescar con los filtros de la semana actual
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    handleSearch({ dateFrom: fmt(monday), dateTo: fmt(sunday) });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Comprobantes</h1>
          <p className="text-gray-600 mt-1">
            Busque y anule comprobantes electrónicos emitidos
          </p>
        </div>
        
        <Button 
          onClick={() => setIsNewVoucherModalOpen(true)}
          className="w-fit"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Comprobante
        </Button>
      </div>

      <VoucherSearch onSearch={handleSearch} loading={loading} />
      
      <VoucherList 
        vouchers={vouchers} 
        onCancel={handleCancel} 
        loading={loading || cancelLoading} 
      />

      <ManualVoucherModal 
        isOpen={isNewVoucherModalOpen}
        onClose={() => setIsNewVoucherModalOpen(false)}
        onSuccess={handleNewVoucherSuccess}
      />
    </div>
  );
};